import React, { useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import SingleHour from "./SingleHour";
import { weekDayMap } from "../../consts";
import { useAuthStore } from "../../store";
import BatteryIndicator from "./BatteryIndicator";
import "../../assets/style/Timetable.css";

const MobileTimetableView = ({
  schedule,
  handleOpenSignUpModal,
  setValues,
  values,
  setLessonData,
  weekDate,
  weekOffset,
  setWeekOffset,
}) => {
  const [selectedDay, setSelectedDay] = useState(0); // 0 = Monday

  const { isAdmin } = useAuthStore((state) => ({
    isAdmin: state.isAdmin,
  }));

  const dayNames = [
    "Pon",
    "Wto",
    "Śro",
    "Czw",
    "Pią",
    "Sob",
    "Nie",
  ];

  const isLessonInPast = (lesson) => {
    const currentTime = new Date();
    const lessonStartTime = new Date(
      lesson.start.year,
      lesson.start.month - 1,
      lesson.start.day,
      lesson.start.hour,
      lesson.start.minute,
      0
    );
    return lessonStartTime <= currentTime;
  };

  const handleDayChange = (event, newValue) => {
    setSelectedDay(newValue);
  };

  const handlePrevDay = () => {
    setSelectedDay((prev) => (prev === 0 ? 6 : prev - 1));
  };

  const handleNextDay = () => {
    setSelectedDay((prev) => (prev === 6 ? 0 : prev + 1));
  };

  // Filter lessons for the selected day
  const getLessonsForDay = () => {
    const dayName = weekDayMap[selectedDay];
    const dayLessons = {};

    // Add null/undefined checks for schedule.objects
    if (!schedule || !schedule.objects) {
      return dayLessons;
    }

    Object.keys(schedule.objects).forEach((hourKey) => {
      const hourLessons = schedule.objects[hourKey];

      // Check if hourLessons exists and is an array
      if (Array.isArray(hourLessons)) {
        const lessonsInHour = hourLessons.filter(
          (lesson) => lesson.week_day === dayName
        );
        if (lessonsInHour.length > 0) {
          dayLessons[hourKey] = lessonsInHour;
        }
      }
    });

    return dayLessons;
  };

  const dayLessons = getLessonsForDay();

  return (
    <div className="mobile-timetable">
      {/* Week Navigation */}
      <div className="mobile-week-navigation">
        <button
          className="mobile-week-nav-btn"
          onClick={() => setWeekOffset(weekOffset - 1)}
          aria-label="Poprzedni tydzień"
        >
          <FaAngleLeft />
        </button>
        <div className="mobile-week-date">
          {weekDate.start} - {weekDate.end}
        </div>
        <button
          className="mobile-week-nav-btn"
          onClick={() => setWeekOffset(weekOffset + 1)}
          aria-label="Następny tydzień"
        >
          <FaAngleRight />
        </button>
      </div>

      {/* Day Navigation */}
      <div className="mobile-day-navigation">
        <button
          className="mobile-day-nav-btn"
          onClick={handlePrevDay}
          aria-label="Poprzedni dzień"
        >
          <FaAngleLeft />
        </button>
        <Box sx={{ flexGrow: 1, maxWidth: "100%", overflow: "auto" }}>
          <Tabs
            value={selectedDay}
            onChange={handleDayChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minWidth: "50px",
                fontSize: "0.875rem",
                padding: "8px 12px",
              },
            }}
          >
            {dayNames.map((day, index) => (
              <Tab key={index} label={day} />
            ))}
          </Tabs>
        </Box>
        <button
          className="mobile-day-nav-btn"
          onClick={handleNextDay}
          aria-label="Następny dzień"
        >
          <FaAngleRight />
        </button>
      </div>

      {/* Lessons for Selected Day */}
      <div className="mobile-lessons-container">
        {Object.keys(dayLessons).length === 0 ? (
          <div className="mobile-no-lessons">
            Brak zajęć w tym dniu
          </div>
        ) : (
          Object.keys(dayLessons).map((hourKey) => {
            const lessons = dayLessons[hourKey];
            return (
              <div key={hourKey} className="mobile-hour-group">
                {lessons.map((lesson) => {
                  const addLeadingZeros = (num) => String(num).padStart(2, "0");
                  const start = `${addLeadingZeros(
                    lesson.start.hour
                  )}:${addLeadingZeros(lesson.start.minute)}`;
                  const end = `${addLeadingZeros(
                    lesson.end.hour
                  )}:${addLeadingZeros(lesson.end.minute)}`;

                  const currentPeople = Number(lesson.current_people) || 0;
                  const maxPeople = Number(lesson.lesson_type.max_people) || 999;
                  const isFull = currentPeople >= maxPeople;
                  const isPast = !isAdmin && isLessonInPast(lesson);

                  return (
                    <div
                      key={lesson.lesson_id}
                      className={`mobile-lesson-card ${
                        isPast
                          ? "past-lesson"
                          : isFull
                          ? "full-lesson"
                          : "clickable"
                      }`}
                      onClick={() => {
                        if (!isPast) {
                          handleOpenSignUpModal(lesson);
                        }
                      }}
                      style={{
                        borderLeftColor: lesson.lesson_type.bg_color || "#9e9e9e",
                      }}
                    >
                      <div className="mobile-lesson-header">
                        <div className="mobile-lesson-name">
                          {lesson.lesson_type.friendly_name}
                          {lesson.payment_only_online &&
                            !lesson.lesson_type.is_free && (
                              <span
                                className="online-only-indicator"
                                title="Płatność tylko online"
                              >
                                💳
                              </span>
                            )}
                        </div>
                        <div className="mobile-lesson-time">
                          {start} - {end}
                        </div>
                      </div>
                      <div className="mobile-lesson-footer">
                        <div className="mobile-lesson-slots">
                          {isAdmin ? (
                            `${currentPeople}/${maxPeople}`
                          ) : (
                            <BatteryIndicator
                              currentPeople={currentPeople}
                              maxPeople={maxPeople}
                            />
                          )}
                        </div>
                        {lesson.waitlist_count > 0 && (
                          <div
                            className="waitlist-indicator"
                            title="Lista oczekujących"
                          >
                            ⏳ {lesson.waitlist_count}
                          </div>
                        )}
                      </div>
                      {isPast && (
                        <div className="past-lesson-text">
                          Zajęcia się odbyły
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileTimetableView;
